import { useCallback, useEffect, useState } from "react";
import { Activity, BarChart3, Eye, MousePointerClick, RefreshCw, ShieldCheck, Smartphone, Users } from "lucide-react";
import { api } from "@/lib/api";

const PERIODS = [7, 30, 90];

function title(value, channel = "all") {
  if (!value) return "Unknown";
  if (value === "/") {
    if (channel === "website") return "Website home (/)";
    if (channel === "app") return "PWA start / sign-in (/)";
    return "Website home / PWA start (/)";
  }
  const formatted = String(value).replace(/^section:/, "").replace(/^(home|navigation)\./, "").replace(/[-_.:/]+/g, " ").replace(/\b\w/g, c => c.toUpperCase()).trim();
  return formatted || "Unknown";
}

function MetricCard({ Icon, label, value, hint }) {
  return <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-[#5C6661]">{label}</span>
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#EEF4F0] text-[#2C4C3B]"><Icon aria-hidden="true" className="h-5 w-5"/></span>
    </div>
    <p className="mt-4 text-3xl font-semibold tabular-nums text-[#1C201E]">{Number(value || 0).toLocaleString()}</p>
    <p className="mt-1 text-xs text-[#6D756F]">{hint}</p>
  </div>;
}

function RankedList({ title: heading, description, items, channel = "all", details, empty = "No activity recorded yet." }) {
  const max = Math.max(...(items || []).map(item => item.count), 1);
  return <section className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
    <h2 className="font-serif-display text-xl text-[#1C201E]">{heading}</h2>
    <p className="mt-1 text-sm text-[#5C6661]">{description}</p>
    {!items?.length ? <p className="mt-6 rounded-xl bg-[#FAF9F6] p-4 text-sm text-[#5C6661]">{empty}</p> :
      <ol className="mt-5 space-y-4">
        {items.map((item, index) => <li key={item.name} className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-4 gap-y-2">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="w-5 shrink-0 text-xs tabular-nums text-[#8A938D]">{index + 1}</span>
              <span className="truncate text-sm font-medium text-[#1C201E]" title={item.name}>{title(item.name, channel)}</span>
            </div>
            {details && <p className="ml-7 mt-1 text-xs text-[#6D756F]">{details(item)}</p>}
            <div className="ml-7 mt-2 h-2 overflow-hidden rounded-full bg-[#EEF0ED]" aria-hidden="true">
              <div className="h-full rounded-full bg-[#4F7A63]" style={{ width: `${Math.max(4, (item.count / max) * 100)}%` }}/>
            </div>
          </div>
          <span className="text-sm font-semibold tabular-nums text-[#2C4C3B]" aria-label={`${item.count} interactions`}>{item.count.toLocaleString()}</span>
        </li>)}
      </ol>}
  </section>;
}

function duration(seconds) {
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(seconds < 600 ? 1 : 0)} min`;
  return `${(seconds / 3600).toFixed(1)} hr`;
}

function DwellList({ items }) {
  const max = Math.max(...(items || []).map(item => item.total_seconds), 1);
  return <section className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
    <h2 className="font-serif-display text-xl text-[#1C201E]">Longest-read website sections</h2>
    <p className="mt-1 text-sm text-[#5C6661]">Active foreground reading time, excluding time while the tab is hidden.</p>
    {!items?.length ? <p className="mt-6 rounded-xl bg-[#FAF9F6] p-4 text-sm text-[#5C6661]">Reading-time data will appear as visitors browse the website.</p> :
      <ol className="mt-5 space-y-4">
        {items.map((item, index) => <li key={item.name} className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-4 gap-y-2">
          <div className="min-w-0"><div className="flex items-baseline gap-2"><span className="w-5 shrink-0 text-xs tabular-nums text-[#8A938D]">{index + 1}</span><span className="truncate text-sm font-medium text-[#1C201E]">{title(item.name)}</span></div>
            <p className="ml-7 mt-1 text-xs text-[#6D756F]">{duration(item.average_seconds)} average · {item.sessions.toLocaleString()} {item.sessions === 1 ? "session" : "sessions"}</p>
            <div className="ml-7 mt-2 h-2 overflow-hidden rounded-full bg-[#EEF0ED]" aria-hidden="true"><div className="h-full rounded-full bg-[#4F7A63]" style={{ width: `${Math.max(4, item.total_seconds / max * 100)}%` }}/></div>
          </div>
          <span className="text-sm font-semibold tabular-nums text-[#2C4C3B]" aria-label={`${duration(item.total_seconds)} total active time`}>{duration(item.total_seconds)}</span>
        </li>)}
      </ol>}
  </section>;
}

export default function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [channel, setChannel] = useState("all");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading"); setError("");
    try {
      const response = await api.get(`/analytics/summary?days=${days}`);
      setData(response.data); setState("ready");
    } catch (err) {
      setError(err?.response?.data?.detail || "Usage analytics could not be loaded.");
      setState("error");
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const report = channel === "all" ? data : (data?.channels?.[channel] || data);
  const totals = report?.totals || data?.totals || {};
  const visiblePlatforms = !data ? [] : channel === "all" ? data.platforms : data.platforms.filter(row => channel === "website" ? row.name === "website" : row.name !== "website");

  return <div className="space-y-6">
    <section className="rounded-2xl border border-[#D8E4DC] bg-[#F2F7F3] p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[#2C4C3B]"><BarChart3 aria-hidden="true" className="h-5 w-5"/><h2 className="font-serif-display text-2xl">Usage priorities</h2></div>
          <p className="mt-2 text-sm leading-6 text-[#4E5B54]">See which website and BBC Connect app features people use most, where website visitors stop, and which sections receive attention.</p>
        </div>
        <button onClick={load} disabled={state === "loading"} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[#B7C9BE] bg-white px-4 text-sm font-semibold text-[#2C4C3B] hover:bg-[#F8FBF9] disabled:cursor-wait disabled:opacity-60">
          <RefreshCw aria-hidden="true" className={`h-4 w-4 ${state === "loading" ? "animate-spin" : ""}`}/>Refresh
        </button>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2" aria-label="Reporting period">
        {PERIODS.map(period => <button key={period} onClick={() => setDays(period)} aria-pressed={days === period} className={`min-h-11 rounded-full px-4 text-sm font-semibold ${days === period ? "bg-[#2C4C3B] text-white" : "border border-[#B7C9BE] bg-white text-[#2C4C3B] hover:bg-[#F8FBF9]"}`}>Last {period} days</button>)}
      </div>
    </section>

    {state === "error" && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>}
    {state === "loading" && !data && <div className="grid min-h-56 place-items-center rounded-2xl border border-[#E5E0D8] bg-white text-sm text-[#5C6661]"><span className="inline-flex items-center gap-2"><RefreshCw aria-hidden="true" className="h-4 w-4 animate-spin"/>Loading usage report&</span></div>}

    {data && <>
      <section className="flex flex-wrap items-center gap-2 rounded-2xl border border-[#E5E0D8] bg-white p-3" aria-label="Analytics channel">
        <span className="px-2 text-xs font-semibold uppercase tracking-wide text-[#6D756F]">Show report for</span>
        {[["all", "All channels"], ["website", "Website"], ["app", "PWA / mobile"]].map(([key, label]) => <button key={key} onClick={() => setChannel(key)} aria-pressed={channel === key} className={`min-h-11 rounded-xl px-4 text-sm font-semibold ${channel === key ? "bg-[#2C4C3B] text-white" : "text-[#2C4C3B] hover:bg-[#F3EFEA]"}`}>{label}</button>)}
      </section>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard Icon={Users} label="Sessions" value={totals.sessions} hint="Distinct visits and app launches"/>
        <MetricCard Icon={MousePointerClick} label="Feature clicks" value={totals.feature_clicks} hint="Intentional feature interactions"/>
        <MetricCard Icon={Eye} label="Pages & screens" value={totals.views} hint="Website page and app screen views"/>
        <MetricCard Icon={Activity} label="Known users" value={totals.identified_users} hint="Signed-in accounts; anonymous use remains counted"/>
      </div>

      <section className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2"><Smartphone aria-hidden="true" className="h-5 w-5 text-[#2C4C3B]"/><h2 className="font-serif-display text-xl">Channel overview</h2></div>
        {!visiblePlatforms.length ? <p className="mt-4 text-sm text-[#5C6661]">Data will appear after people use the staged website or PWA.</p> :
          <div className="mt-4 overflow-x-auto"><table className="w-full min-w-[560px] text-left text-sm">
            <caption className="sr-only">Sessions, feature clicks, and events by platform</caption>
            <thead><tr className="border-b border-[#E5E0D8] text-xs uppercase tracking-wide text-[#6D756F]"><th className="py-3 pr-4">Channel</th><th className="px-4 py-3 text-right">Sessions</th><th className="px-4 py-3 text-right">Feature clicks</th><th className="py-3 pl-4 text-right">All events</th></tr></thead>
            <tbody>{visiblePlatforms.map(row => <tr key={row.name} className="border-b border-[#F0ECE6] last:border-0"><th className="py-4 pr-4 font-medium text-[#1C201E]">{title(row.name)}</th><td className="px-4 py-4 text-right tabular-nums">{row.sessions.toLocaleString()}</td><td className="px-4 py-4 text-right tabular-nums">{row.feature_clicks.toLocaleString()}</td><td className="py-4 pl-4 text-right tabular-nums">{row.events.toLocaleString()}</td></tr>)}</tbody>
          </table></div>}
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <RankedList title="Most-used features" description="Use this ranking to prioritize improvements and testing." items={report.top_features} channel={channel}/>
        <RankedList title="Pages and app screens" description="The most frequently viewed destinations across both experiences." items={report.top_pages} channel={channel}/>
        {channel === "app" ? <RankedList title="Common app exit screens" description="The last app screen recorded before a session ended." items={report.exit_points} channel={channel}/> :
          <RankedList title="Website scroll stopping points" description="The last visible website section before visitors leave, ranked by frequency." items={report.scroll_stops} channel="website" details={item => `Average maximum scroll: ${item.average_scroll_depth}%`}/>}
        {channel !== "app" && <RankedList title="Website section reach" description="How many visits reached each public website section." items={report.section_reach} channel="website"/>}
        {channel !== "app" && <DwellList items={report.section_dwell}/>}
      </div>

      <section className="flex gap-3 rounded-2xl border border-[#D8E4DC] bg-white p-5 text-sm text-[#4E5B54]">
        <ShieldCheck aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-[#2C4C3B]"/>
        <div><h2 className="font-semibold text-[#1C201E]">Privacy by design</h2><p className="mt-1 leading-6">This report stores aggregate interaction events for {data.privacy.retention_days} days. It does not collect typed form content, prayer or message text, search terms, passwords, IP addresses, or precise pointer movement. Individual browsing timelines are not available.</p></div>
      </section>
    </>}
  </div>;
}
